import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Mail } from "lucide-react";
import { isAuthenticated, resendVerificationEmail } from "../../shared/api/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Post-registration "check your inbox" page. The owner is already logged in (register returns a
 * session) but must still confirm the e-mail — this page makes that step visible before dropping
 * them into the app, where the waiting banner takes over. Reachable only right after registration
 * (it reads the e-mail from router state); a direct hit with no session bounces to /login.
 */
export function RegisteredPage() {
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email;
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  // Guard: only meaningful for a just-registered, logged-in owner.
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  async function onResend() {
    setResending(true);
    try {
      await resendVerificationEmail();
      setResent(true);
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 font-sans">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 text-3xl font-extrabold tracking-tight text-primary">
            <span aria-hidden="true">✚</span> Addy HIS
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Hệ thống quản lý phòng khám
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Tạo phòng khám thành công 🎉</CardTitle>
            <CardDescription>
              Chúng tôi đã gửi email kích hoạt{email ? " tới " : ""}
              {email ? <strong className="text-foreground">{email}</strong> : ""}. Vui lòng mở email và
              nhấn vào liên kết để xác nhận địa chỉ (liên kết có hiệu lực trong 24 giờ).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-foreground">
              Bạn có thể vào sử dụng hệ thống ngay bây giờ — việc kích hoạt email giúp
              bảo mật và khôi phục tài khoản về sau.
            </div>

            <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
              <Mail className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
              <div>
                Không thấy email? Kiểm tra hộp thư rác hoặc mục Quảng cáo (Promotions),
                hoặc gửi lại bên dưới.
              </div>
            </div>

            <Button asChild className="w-full">
              <Link to="/dashboard">Vào hệ thống</Link>
            </Button>

            {resent ? (
              <p className="text-center text-sm text-muted-foreground">
                Đã gửi lại email kích hoạt.
              </p>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={onResend}
                disabled={resending}
              >
                {resending ? "Đang gửi…" : "Gửi lại email kích hoạt"}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
